import React, { useState, useMemo } from 'react';
import { Box, Link, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import styled from 'styled-components';
import {
  formatCompanyInfo,
  FooterConfig,
} from '../../../Config/FooterConfig';
import { useFooterConfigReactQuery, getDefaultFooterConfig } from './reactQuery/FooterReactQuery';
import { SystemRoutes } from '../../../Utils/RouteUtils';

import config from "../../../Config/Constants";
import logo from '../../../Assets/logo-ati.svg';
import { LoadingButton } from '@mui/lab';
import { QueryClient, QueryClientProvider } from 'react-query';

interface SGDFooterProps {
  onCallPortalClick?: () => void;
}

const FooterContainer = styled(Box)`
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  flex-shrink: 0;
  height: 10vh;
`;

const TopContainer = styled(Box)<{ isDarkMode?: boolean }>`
  background-color: ${props => props.isDarkMode ? '#171717' : '#FFF'};
  padding: 16px 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px solid #cfc9c965;
  
  @media (max-width: 600px) {
    padding: 12px 16px;
  }
`;

const BottomContainer = styled(Box)`
  background-color: #171717;
  padding: 16px 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 1024px) {
    gap: 16px;
    padding: 16px 24px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 16px;
    gap: 16px;
  }
`;

const CompanyInfo = styled.p`
  color: #d9dcdd;
  font-size: 11px;
  margin: 0;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  flex: 1;
  text-align: center;
  
  @media (max-width: 1024px) {
    font-size: 10px;
  }
  
  @media (max-width: 768px) {
    white-space: normal;
    font-size: 12px;
    flex: auto;
  }
`;

const SocialIconsContainer = styled(Box)`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  
  & a {
    color: #d9dcdd;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease, transform 0.1s ease;
    border-radius: 50%;
    padding: 8px;
    
    &:hover {
      color: #ffffff;
      background-color: rgba(255, 255, 255, 0.1);
      transform: scale(1.1);
    }
    
    &:active {
      transform: scale(0.95);
    }
  }
`;

const FooterComponent: React.FC<SGDFooterProps> = ({ onCallPortalClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { data: configFooterData } = useFooterConfigReactQuery();
  
  const configFooter: FooterConfig = configFooterData || getDefaultFooterConfig();

  const [appVersion, setAppVersion] = useState<string>("");

  const companyInfoText = useMemo(
    () => formatCompanyInfo(configFooter.companyInfo),
    [configFooter.companyInfo]
  );

  React.useEffect(() => {
    if (config.VERSION) {
      const versionParts = config.VERSION.split("-");
      const versionDisplay = versionParts.length > 1 ? versionParts[1] : config.VERSION;
      setAppVersion(versionDisplay);
    }
  }, []);

  const handleCallPortalClick = () => {
    if (onCallPortalClick) {
      onCallPortalClick();
    }
    window.open(configFooter.callPortalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleLogoDashboardClick = () => {
    navigate(SystemRoutes.DASHBOARD_OVERVIEW);
  };
  return (
    <FooterContainer theme={theme}>
      <TopContainer isDarkMode={theme.palette.mode === 'dark'}>
        <LoadingButton
          variant="text"
          onClick={handleCallPortalClick}
          title="Abrir portal de chamados em uma nova aba para reportar bugs ou problemas"
          aria-label="Abrir portal de chamados"
          sx={{padding: '6px 0px'}}
        >
          Abrir um Chamado
        </LoadingButton>
        <Box sx={{padding:'0 24px', color: theme.palette.text.secondary, fontSize: '14px'}}>
          {appVersion}
        </Box>
      </TopContainer>

      <BottomContainer>
        <Box 
          onClick={handleLogoDashboardClick}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minWidth: isMobile ? 'auto' : '67px', 
            height: isMobile ? 'auto' : '25.629px',
            order: isMobile ? 1 : 0,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.8,
            },
            '&:active': {
              opacity: 0.6,
            }
          }}
          title="Ir para Dashboard"
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleLogoDashboardClick();
            }
          }}
        >
          <img 
            src={logo}
            alt="ATI Logo - Clique para ir ao Dashboard"
            style={{ height: '25.629px', width: '66.99px', pointerEvents: 'none' }}
          />
        </Box>

        <CompanyInfo>
          {companyInfoText}
        </CompanyInfo>

        <SocialIconsContainer sx={{ order: isMobile ? 2 : 'auto' }}>
          <Link
            href={configFooter.linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="LinkedIn ATI Automação"
            aria-label="LinkedIn ATI"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <LinkedInIcon sx={{ fontSize: '24px' }} />
          </Link>
          <Link
            href={configFooter.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram ATI Automação"
            aria-label="Instagram ATI"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <InstagramIcon sx={{ fontSize: '24px' }} />
          </Link>
        </SocialIconsContainer>
      </BottomContainer>
    </FooterContainer>
  );
};

export default function SGDFooter() {
    const queryClient = new QueryClient();
    return(
        <QueryClientProvider client={queryClient}>
            <FooterComponent/>
        </QueryClientProvider>
    )
}
